/**
 * Gallery Side Navigation
 * Handles the left/right navigation arrows for the media gallery
 * Integrates with existing Shopify Dawn slider functionality
 */

class GallerySideNavigation {
  constructor(container) {
    this.container = container;
    this.sliderComponent = container.querySelector('slider-component');
    this.slider = this.sliderComponent?.querySelector('.slider');
    this.slides = this.slider?.querySelectorAll('.slider__slide');
    this.prevZone = container.querySelector('.gallery-side-nav__zone--prev');
    this.nextZone = container.querySelector('.gallery-side-nav__zone--next');
    this.prevButton = container.querySelector('.gallery-side-nav__button--prev');
    this.nextButton = container.querySelector('.gallery-side-nav__button--next');
    
    if (this.slider && this.slides && this.slides.length > 1) {
      this.init();
    }
  }

  init() {
    this.bindEvents();
    this.updateArrowVisibility();
  }

  bindEvents() {
    // Bind click events to the large navigation zones
    this.prevZone?.addEventListener('click', (e) => {
      e.preventDefault();
      console.log('Previous zone clicked');
      this.navigatePrevious();
      setTimeout(() => this.updateArrowVisibility(), 100);
    });

    this.nextZone?.addEventListener('click', (e) => {
      e.preventDefault();
      console.log('Next zone clicked');
      this.navigateNext();
      setTimeout(() => this.updateArrowVisibility(), 100);
    });

    // Add keyboard support for zones
    this.prevZone?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.navigatePrevious();
        setTimeout(() => this.updateArrowVisibility(), 100);
      }
    });

    this.nextZone?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.navigateNext();
        setTimeout(() => this.updateArrowVisibility(), 100);
      }
    });

    // Listen for existing slider button clicks to keep arrows in sync
    const existingButtons = this.sliderComponent?.querySelectorAll('.slider-button');
    existingButtons?.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(() => this.updateArrowVisibility(), 100);
      });
    });

    // Listen for thumbnail clicks to keep arrows in sync
    const thumbnails = this.container.closest('media-gallery')?.querySelectorAll('.thumbnail');
    thumbnails?.forEach(thumbnail => {
      thumbnail.addEventListener('click', () => {
        setTimeout(() => this.updateArrowVisibility(), 100);
      });
    });

    // Listen for slider scroll events to update arrow states
    this.slider?.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.updateArrowVisibility(), 50);
    });
  }

  navigatePrevious() {
    console.log('Previous arrow clicked!');
    
    // Try multiple selectors for Dawn buttons
    const selectors = [
      '.slider-button--prev',
      '.slider-button[name="previous"]',
      'button[name="previous"]',
      '[aria-label*="previous"]'
    ];
    
    let existingPrevButton = null;
    for (const selector of selectors) {
      existingPrevButton = this.sliderComponent?.querySelector(selector) || 
                          document.querySelector(selector);
      if (existingPrevButton) {
        console.log('Found prev button with selector:', selector);
        break;
      }
    }
    
    if (existingPrevButton && !existingPrevButton.disabled) {
      console.log('Clicking prev button');
      existingPrevButton.click();
      
      // Wait and check if slide changed
      setTimeout(() => {
        console.log('Checking if slide changed...');
        // If no change, try alternative methods
        this.tryAlternativeNavigation('prev');
      }, 100);
    } else {
      console.log('No prev button found or disabled');
      // Fallback: try thumbnail navigation
      this.tryThumbnailNavigation('prev');
    }
  }

  navigateNext() {
    console.log('Next arrow clicked!');
    
    // Try multiple selectors for Dawn buttons
    const selectors = [
      '.slider-button--next',
      '.slider-button[name="next"]', 
      'button[name="next"]',
      '[aria-label*="next"]'
    ];
    
    let existingNextButton = null;
    for (const selector of selectors) {
      existingNextButton = this.sliderComponent?.querySelector(selector) || 
                          document.querySelector(selector);
      if (existingNextButton) {
        console.log('Found next button with selector:', selector);
        break;
      }
    }
    
    if (existingNextButton && !existingNextButton.disabled) {
      console.log('Clicking next button');
      existingNextButton.click();
      
      // Wait and check if slide changed
      setTimeout(() => {
        console.log('Checking if slide changed...');
        // If no change, try alternative methods
        this.tryAlternativeNavigation('next');
      }, 100);
    } else {
      console.log('No next button found or disabled');
      // Fallback: try thumbnail navigation
      this.tryThumbnailNavigation('next');
    }
  }

  tryThumbnailNavigation(direction) {
    console.log('Trying thumbnail navigation:', direction);
    const thumbnails = document.querySelectorAll('.thumbnail');
    const activeThumbnail = document.querySelector('.thumbnail[aria-current="true"]');
    
    if (thumbnails.length > 0 && activeThumbnail) {
      const activeIndex = Array.from(thumbnails).indexOf(activeThumbnail);
      let targetIndex = direction === 'next' ? activeIndex + 1 : activeIndex - 1;
      
      if (targetIndex >= 0 && targetIndex < thumbnails.length) {
        console.log('Clicking thumbnail', targetIndex);
        thumbnails[targetIndex].click();
        return true;
      }
    }
    return false;
  }

  tryAlternativeNavigation(direction) {
    console.log('Trying alternative navigation methods for:', direction);
    
    // Method 1: Try thumbnail navigation first
    if (this.tryThumbnailNavigation(direction)) {
      console.log('Thumbnail navigation successful');
      return;
    }
    
    // Method 2: Try direct slide manipulation
    const slides = this.slider?.querySelectorAll('.slider__slide');
    const activeSlide = this.slider?.querySelector('.slider__slide.is-active');
    
    if (slides && slides.length > 1 && activeSlide) {
      console.log('Trying direct slide manipulation');
      const activeIndex = Array.from(slides).indexOf(activeSlide);
      let targetIndex;
      
      if (direction === 'next') {
        targetIndex = activeIndex < slides.length - 1 ? activeIndex + 1 : 0;
      } else {
        targetIndex = activeIndex > 0 ? activeIndex - 1 : slides.length - 1;
      }
      
      console.log(`Switching from slide ${activeIndex} to ${targetIndex}`);
      
      // Remove active from current slide
      slides.forEach(slide => slide.classList.remove('is-active'));
      
      // Add active to target slide
      slides[targetIndex].classList.add('is-active');
      
      // Scroll to target slide
      slides[targetIndex].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest',
        inline: 'start'
      });
      
      // Update thumbnail if exists
      const thumbnails = document.querySelectorAll('.thumbnail');
      if (thumbnails[targetIndex]) {
        document.querySelectorAll('.thumbnail').forEach(thumb => 
          thumb.removeAttribute('aria-current'));
        thumbnails[targetIndex].setAttribute('aria-current', 'true');
      }
      
      // Update counter
      const counterCurrent = document.querySelector('.slider-counter--current');
      if (counterCurrent) {
        counterCurrent.textContent = targetIndex + 1;
      }
      
      console.log('Direct slide manipulation completed');
      return;
    }
    
    // Method 3: Try scrolling the slider container
    if (this.slider) {
      console.log('Trying slider scroll');
      const slideWidth = this.slider.offsetWidth;
      const scrollAmount = direction === 'next' ? slideWidth : -slideWidth;
      
      this.slider.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
    
    console.log('All navigation methods attempted');
  }



  updateArrowVisibility() {
    if (!this.prevZone || !this.nextZone) return;

    // Check state of existing Dawn buttons to mirror their behavior
    const existingPrevButton = this.sliderComponent?.querySelector('.slider-button--prev');
    const existingNextButton = this.sliderComponent?.querySelector('.slider-button--next');

    // Update previous zone based on existing button state
    if (existingPrevButton && existingPrevButton.disabled) {
      this.prevZone.style.pointerEvents = 'none';
      this.prevZone.style.opacity = '0.3';
      this.prevZone.setAttribute('aria-disabled', 'true');
    } else {
      this.prevZone.style.pointerEvents = 'auto';
      this.prevZone.style.opacity = '1';
      this.prevZone.setAttribute('aria-disabled', 'false');
    }

    // Update next zone based on existing button state
    if (existingNextButton && existingNextButton.disabled) {
      this.nextZone.style.pointerEvents = 'none';
      this.nextZone.style.opacity = '0.3';
      this.nextZone.setAttribute('aria-disabled', 'true');
    } else {
      this.nextZone.style.pointerEvents = 'auto';
      this.nextZone.style.opacity = '1';
      this.nextZone.setAttribute('aria-disabled', 'false');
    }
  }
}

// Initialize side navigation for all media galleries
function initializeGallerySideNavigation() {
  console.log('🔍 Starting side navigation initialization...');
  
  const mediaGalleries = document.querySelectorAll('media-gallery');
  console.log(`Found ${mediaGalleries.length} media galleries`);
  
  mediaGalleries.forEach((gallery, index) => {
    console.log(`Checking gallery ${index + 1}...`);
    const sideNav = gallery.querySelector('.gallery-side-nav');
    console.log(`- Side nav element found:`, !!sideNav);
    
    if (sideNav) {
      console.log(`- Media count:`, sideNav.getAttribute('data-media-count'));
      console.log(`- Element classes:`, sideNav.className);
      
      new GallerySideNavigation(gallery);
      console.log('✅ Side navigation initialized for gallery', index + 1);
    } else {
      console.log('❌ No side nav element found in gallery', index + 1);
    }
  });
}

// Enhanced initialization with retry mechanism
function initWithRetry(maxRetries = 3, delay = 500) {
  let attempts = 0;
  
  function tryInit() {
    attempts++;
    
    const galleries = document.querySelectorAll('media-gallery');
    if (galleries.length > 0) {
      initializeGallerySideNavigation();
      return;
    }
    
    if (attempts < maxRetries) {
      setTimeout(tryInit, delay);
    } 
  }
  
  tryInit();
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initWithRetry());
} else {
  initWithRetry();
}

// Re-initialize when new content is loaded (for AJAX/dynamic content)
document.addEventListener('shopify:section:load', initializeGallerySideNavigation);

// Export for manual initialization if needed
window.GallerySideNavigation = GallerySideNavigation;
window.initializeGallerySideNavigation = initializeGallerySideNavigation;

// Test original Dawn functionality
window.testOriginalSlider = function() {
  console.log('=== Testing Original Dawn Slider ===');
  
  const galleries = document.querySelectorAll('media-gallery');
  const sliderComponents = document.querySelectorAll('slider-component');
  const originalPrev = document.querySelectorAll('.slider-button--prev');
  const originalNext = document.querySelectorAll('.slider-button--next');
  const slides = document.querySelectorAll('.slider__slide');
  
  console.log(`Found ${galleries.length} media galleries`);
  console.log(`Found ${sliderComponents.length} slider components`);
  console.log(`Found ${slides.length} slides`);
  console.log(`Original Dawn buttons: ${originalPrev.length} prev, ${originalNext.length} next`);
  
  // Check if buttons are disabled
  if (originalNext.length > 0) {
    console.log('Next button disabled?', originalNext[0].disabled);
    if (!originalNext[0].disabled) {
      console.log('Testing original NEXT button...');
      originalNext[0].click();
    }
  }
  
  if (originalPrev.length > 0) {
    console.log('Prev button disabled?', originalPrev[0].disabled);
  }
  
  return {
    galleries: galleries.length,
    sliderComponents: sliderComponents.length,
    slides: slides.length,
    originalButtons: { prev: originalPrev.length, next: originalNext.length },
    testOriginalNext: () => {
      console.log('Testing original next button...');
      if (originalNext.length > 0 && !originalNext[0].disabled) {
        originalNext[0].click();
        console.log('Clicked next button');
      } else {
        console.log('Next button disabled or not found');
      }
    },
    testOriginalPrev: () => {
      console.log('Testing original prev button...');
      if (originalPrev.length > 0 && !originalPrev[0].disabled) {
        originalPrev[0].click();
        console.log('Clicked prev button');
      } else {
        console.log('Prev button disabled or not found');
      }
    }
  };
};

// Test side navigation functionality with zones
window.testSideNavigation = function() {
  console.log('=== Testing Side Navigation with Large Zones ===');
  
  const sideNavs = document.querySelectorAll('.gallery-side-nav');
  const sideZones = document.querySelectorAll('.gallery-side-nav__zone');
  const sideButtons = document.querySelectorAll('.gallery-side-nav__button');
  const originalButtons = document.querySelectorAll('.slider-button--prev, .slider-button--next');
  
  console.log(`Found ${sideNavs.length} side nav containers`);
  console.log(`Found ${sideZones.length} clickable zones`);
  console.log(`Found ${sideButtons.length} visual arrow buttons`);
  console.log(`Found ${originalButtons.length} original Dawn buttons`);
  
  if (sideZones.length > 0) {
    console.log('✅ Small edge clickable zones found!');
    console.log('You can now click on the left or right edges of the image (80px zones)');
    return {
      sideNavs: sideNavs.length,
      sideZones: sideZones.length,
      sideButtons: sideButtons.length,
      originalButtons: originalButtons.length,
      testNext: () => {
        const nextZone = document.querySelector('.gallery-side-nav__zone--next');
        if (nextZone) {
          console.log('Testing NEXT zone click...');
          nextZone.click();
        }
      },
      testPrev: () => {
        const prevZone = document.querySelector('.gallery-side-nav__zone--prev');
        if (prevZone) {
          console.log('Testing PREV zone click...');
          prevZone.click();
        }
      },
      showZones: () => {
        document.querySelectorAll('.gallery-side-nav').forEach(nav => {
          nav.classList.add('debug');
        });
        console.log('🎯 Debug zones now visible! Green = left edge (prev), Blue = right edge (next)');
      },
      hideZones: () => {
        document.querySelectorAll('.gallery-side-nav').forEach(nav => {
          nav.classList.remove('debug');
        });
        console.log('👁️ Debug zones hidden');
      }
    };
  } else {
    console.log('❌ No side navigation zones found');
    return false;
  }
};
